import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-01-31 22:57:43 +0100 (Sat, 31 Jan 2026) $
//  Author    : Tobias Schwarzkopf, Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 160223 $
//  Purpose   : parts for tests
// =============================================================================
// ---Third-party imports
import React, { Fragment } from 'react';
import { Header, Divider, Label, Tab, Image, Icon, List, Input, Container } from 'semantic-ui-react';
import KeyHandler, { KEYPRESS, KEYDOWN } from 'react-key-handler';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import { Parts } from 'uix/registry';
import { loadLazy as ll, funcPart, StandalonePart } from 'uix/parts';
import log from 'uix/logging';
import { keys, dict, iterItems, mapItems, values, sum, flatten, sorted, zip } from 'ldash';
import { ucFirst, prettyBytes } from 'uix/utils';
import { Icons, vicon } from 'uix/graphics';
import { Editor, Differ, HeaderSegment, Link, XMenu } from 'uix/components';
import { usePersistent, usePersistentShallow, useSelector, useActions, useApi, useState, useDelay, useEffectSSR as useEffect } from 'uix/hooks';
import { TreeNodeItem } from 'uix/db/components';
import { Plot } from 'uix/modules/stats/plots';
export * from './persistUi';
export { HmrTestAsModule } from './test_hmr';
// ---Globals
export const HmrTestInParts = ()=>{
    return 'HmrTestInParts: ' + 'HMR_CHECK_0';
};
export const TestExplorer = ll(()=>import(/* webpackChunkName: 'tests' */ './explorer'));
export const TestDummy = funcPart(({ go, wiki, partcfg, request })=>{
    if (!wiki) {
        return null;
    }
    let run = ()=>{
        // removal is checked in testProdBuild()
        log.info('*** removed by babel plugin remove-logapi-calls ***');
        log.error('*** removed by babel plugin remove-logapi-calls ***');
        log.exception('*** NOT removed by babel plugin remove-logapi-calls ***');
        go({
            tabname: partcfg.clickTarget
        });
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("span", {
                children: [
                    "This is Tab ",
                    /*#__PURE__*/ _jsx("b", {
                        children: request.tabname
                    }),
                    " with Wiki:",
                    ' '
                ]
            }),
            /*#__PURE__*/ _jsx("b", {
                children: wiki.identifier
            }),
            /*#__PURE__*/ _jsx("div", {
                onClick: run,
                children: "Click me."
            })
        ]
    });
});
let Umbrella = ()=>/*#__PURE__*/ _jsxs("svg", {
        className: "umbrella",
        xmlns: "http://www.w3.org/2000/svg",
        width: "32",
        height: "32",
        viewBox: "0 0 32 32",
        "aria-labelledby": "title",
        children: [
            /*#__PURE__*/ _jsx("title", {
                id: "title",
                children: "Umbrella Icon"
            }),
            /*#__PURE__*/ _jsx("path", {
                d: "M27 14h5c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2v0zM27 14c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2v0 14c0 1.112-0.895 2-2 2-1.112 0-2-0.896-2-2.001v-1.494c0-0.291 0.224-0.505 0.5-0.505 0.268 0 0.5 0.226 0.5 0.505v1.505c0 0.547 0.444 0.991 1 0.991 0.552 0 1-0.451 1-0.991v-14.009c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2c0-5.415 6.671-9.825 15-9.995v-1.506c0-0.283 0.224-0.499 0.5-0.499 0.268 0 0.5 0.224 0.5 0.499v1.506c8.329 0.17 15 4.58 15 9.995h-5z"
            })
        ]
    });
export const TestGraphics = funcPart({
    getGraphics: [
        'types',
        'states',
        'controls',
        'license',
        'connectors',
        'common',
        'tree',
        'icons'
    ]
}, ({ Graphics, go, request })=>{
    let [ui, setUI] = usePersistentShallow('testGraphics', {
        activeIndex: 0,
        __version__: 1
    });
    let { types, states, controls, license, connectors, common, tree, icons } = Graphics;
    let path2grouped = (imported)=>dict(keys(imported).filter((k)=>k.split('/').length == 2).map((k)=>[
                k,
                k.split('/')
            ]));
    let groupGraphics = (imported)=>{
        let groupedImported = {};
        iterItems(path2grouped(imported), (k, [datatype, subtype])=>{
            if (!groupedImported[datatype]) {
                groupedImported[datatype] = {};
            }
            groupedImported[datatype][subtype] = k;
        });
        return groupedImported;
    };
    let groupedTypes = groupGraphics(types);
    let groupedStates = groupGraphics(states);
    const _ = (imported)=>dict(keys(imported).map((k)=>[
                k,
                k
            ]));
    let groupedOther = {
        controls: _(controls),
        common: _(common),
        tree: _(tree),
        icons: _(icons),
        license: _(license),
        connectors: _(connectors)
    };
    let other = {
        ...controls,
        ...common,
        ...tree,
        ...icons,
        ...license,
        ...connectors
    };
    //~ this.pprint({0: groupedTypes, 1: groupedStates, 2: groupedOther}[ui.activeIndex])
    //~ vl.pprint(controls)
    let draw = [
        (imported, subtype, name)=>/*#__PURE__*/ _jsxs(Label, {
                basic: true,
                children: [
                    /*#__PURE__*/ _jsx(Image, {
                        inline: true,
                        centered: true,
                        src: imported[name]
                    }),
                    ' ' + name.split('/').slice(-1)[0]
                ]
            }, name),
        (imported, subtype, name)=>/*#__PURE__*/ _jsx("img", {
                width: "32rem",
                title: subtype,
                src: imported[name],
                style: {
                    verticalAlign: 'middle'
                }
            }, name),
        (imported, subtype, name)=>/*#__PURE__*/ _jsx("img", {
                width: "128rem",
                title: subtype,
                src: imported[name],
                style: {
                    border: '1px solid black'
                }
            }, name)
    ];
    let drawMode = request.query.draw || 0;
    let drawFunc = draw[drawMode];
    let sections = (groupedImported, imported)=>mapItems(groupedImported, (datatype, subtypes)=>{
            const sts = mapItems(subtypes, (subtype, name)=>drawFunc(imported, subtype, name));
            let svgsize = sum(values(subtypes).map((name)=>imported[name].length));
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(Header, {
                        as: "h3",
                        style: {
                            cursor: 'pointer'
                        },
                        image: imported[values(subtypes)[0]],
                        content: ucFirst(datatype),
                        onClick: ()=>go({
                                query: {
                                    draw: (drawMode + 1) % draw.length
                                }
                            })
                    }),
                    sts,
                    /*#__PURE__*/ _jsx(Label, {
                        content: `Size: ${prettyBytes(svgsize)}`,
                        size: "mini",
                        pointing: "left"
                    }),
                    /*#__PURE__*/ _jsx(Divider, {})
                ]
            }, datatype);
        });
    let renderFonts = ()=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Label, {
                    basic: true,
                    icon: "clock outline",
                    content: "sui-clock"
                }),
                /*#__PURE__*/ _jsx(Label, {
                    basic: true,
                    icon: vicon('clock_fa'),
                    content: "vl-clock"
                }),
                /*#__PURE__*/ _jsx(Label, {
                    basic: true,
                    icon: "warehouse",
                    content: "sui-warehouse"
                }),
                flatten([
                    ...keys(Icons).map((name)=>/*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    basic: true,
                                    icon: Icons[name],
                                    content: name
                                }),
                                /*#__PURE__*/ _jsx(Label, {
                                    basic: true,
                                    icon: vicon(name, {
                                        loading: true
                                    }),
                                    content: name
                                })
                            ]
                        }, name))
                ])
            ]
        });
    let pane = (content)=>/*#__PURE__*/ _jsx(Tab.Pane, {
            style: {
                flex: 1,
                overflow: 'auto'
            },
            attached: false,
            children: content
        });
    let panes = [
        {
            menuItem: 'Types',
            render: ()=>pane(sections(groupedTypes, types))
        },
        {
            menuItem: 'States',
            render: ()=>pane(sections(groupedStates, states))
        },
        {
            menuItem: 'Other',
            render: ()=>pane(sections(groupedOther, other))
        },
        {
            menuItem: 'Fonts',
            render: ()=>pane(renderFonts())
        }
    ];
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(Tab, {
            style: {
                display: 'flex',
                flexDirection: 'column',
                flex: 1
            },
            menu: {
                secondary: true,
                pointing: true
            },
            panes: panes,
            activeIndex: ui.activeIndex,
            onTabChange: (e, data)=>setUI({
                    activeIndex: data.activeIndex
                })
        })
    });
});
export const TestEditor = funcPart({
    partStyle: {
        display: 'flex',
        flex: 1
    }
}, ({})=>{
    let [text, setText] = usePersistent('TestText', '');
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        header: "Editor",
        flex: true,
        children: /*#__PURE__*/ _jsx(Editor, {
            flex: true,
            text: text,
            opts: {
                mode: 'markdown'
            },
            onBeforeChange: ({ value })=>setText(value)
        })
    });
});
export const TestDiffer = funcPart({
    partStyle: {
        display: 'flex',
        flex: 1
    }
}, ({})=>{
    //~ let [text, setText] = usePersistent('TestText', '')
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        header: "Differ",
        flex: true,
        portal: "DifferPortal",
        children: /*#__PURE__*/ _jsx(Differ, {
            flex: true,
            textLeft: 'left',
            textMiddle: 'right',
            headLeft: "left file",
            headMiddle: "right file",
            mode: 'markdown',
            xml: false,
            menuPortal: "DifferPortal"
        })
    });
});
export const ListTestPages = ({})=>{
    let tcfg = useSelector(Sel.domainClassCfgs);
    let go = useActions(Actions.go);
    let [searchString, setSearchString] = useState('');
    let [focused, setFocused] = useState(null);
    let tabs = sorted(keys(tcfg.test.tabs)).filter((e)=>e.includes(searchString));
    let onKey = (event)=>{
        if (!focused || tabs.indexOf(focused) == -1) {
            setFocused(tabs[0]);
        } else {
            let idx = tabs.indexOf(focused);
            if (event.key == 'ArrowUp') {
                setFocused(tabs[Math.max(idx - 1, 0)]);
            } else if (event.key == 'ArrowDown') {
                setFocused(tabs[Math.min(idx + 1, tabs.length - 1)]);
            } else if (event.key == 'Enter') {
                go({
                    tabname: focused
                });
            }
        }
    };
    let links = tabs.map((name)=>/*#__PURE__*/ _jsx(List.Item, {
            active: focused == name,
            children: /*#__PURE__*/ _jsx(Link, {
                tabname: name,
                children: name
            })
        }, name));
    let keycodes = [
        'ArrowUp',
        'ArrowDown'
    ];
    let keyhandlers = keycodes.map((k)=>/*#__PURE__*/ _jsx(KeyHandler, {
            keyEventName: KEYDOWN,
            code: k,
            onKeyHandle: onKey
        }, k));
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            keyhandlers,
            /*#__PURE__*/ _jsx(Input, {
                size: "small",
                value: searchString,
                onChange: (event, data)=>setSearchString(data.value),
                ref: (ref)=>ref && ref.focus(),
                onKeyDown: onKey
            }),
            /*#__PURE__*/ _jsx(List, {
                link: true,
                size: "medium",
                children: links
            })
        ]
    });
};
export const TestParts = funcPart({
    getGraphics: [
        'types'
    ],
    partStyle: {
        display: 'flex',
        flex: 1
    }
}, ({ go, request })=>{
    let [filter, setFilter] = usePersistent('partSearchFilter', '');
    let parts = Parts.components;
    let filtered = keys(parts).filter((n)=>n.toLowerCase().includes(filter.toLowerCase()));
    let ptype = request.ctxid || request.id;
    let otid = request.ctxid ? request.id : null;
    let parttype = parts[ptype] ? ptype : null;
    let items = sorted(filtered).slice(0, 7).map((n)=>/*#__PURE__*/ _jsx(XMenu.Item, {
            content: n,
            active: ptype == n,
            onClick: ()=>go(request.ctxid ? {
                    ctxid: n
                } : {
                    id: n
                })
        }, n));
    //~ vl.pprint(request)
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        children: [
            items,
            /*#__PURE__*/ _jsx(XMenu.FilterInput, {
                defaultValue: filter,
                onValueChange: setFilter
            }),
            /*#__PURE__*/ _jsx(XMenu.Input, {
                label: "ID",
                value: request.ctxid && request.id ? request.id : '',
                onValueChange: (v)=>go({
                        ctxid: ptype,
                        id: v
                    })
            })
        ]
    });
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        header: parttype,
        subheader: /*#__PURE__*/ _jsx(TreeNodeItem, {
            otid: otid
        }),
        menu: menu,
        flex: true,
        children: parttype && /*#__PURE__*/ _jsx(StandalonePart, {
            partCfg: {
                style: {
                    display: 'flex',
                    flex: 1,
                    overflow: 'auto'
                }
            },
            type: parttype,
            name: `Test_${parttype}`
        })
    });
});
export const TestUseApi = ({})=>{
    let res = useApi('info');
    return JSON.stringify(res || 'null');
};
export const TestPlots = funcPart({
    partStyle: {
        display: 'flex',
        flex: 1
    }
}, ()=>{
    let [plotName, setPlotName] = useState('zipcapsule');
    let menu = /*#__PURE__*/ _jsx(XMenu, {
        children: /*#__PURE__*/ _jsx(XMenu.Dropdown, {
            options: keys(Plot.PLOTS).map((v)=>({
                    value: v,
                    key: v,
                    text: v
                })),
            value: plotName,
            onSetValue: setPlotName
        })
    });
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        flex: true,
        header: "Plots",
        menu: menu,
        portal: "plotMenuPortal",
        children: /*#__PURE__*/ _jsx(Plot, {
            flex: true,
            name: plotName,
            menuPortal: "plotMenuPortal",
            showMenu: true,
            showLegend: true
        })
    });
});
export const TestRenderWaterfall = ({})=>{
    /* simulate cascading changes on store via effects triggered by rendering
  (test SSR/waitForIdle )
  */ let setDebugData = useActions('setDebugData');
    let waterfall = useSelector((root)=>root.debug.waterfall) || 0;
    useEffect(()=>{
        log.debug('set waterfall', waterfall + 1);
        setDebugData({
            waterfall: waterfall + 1
        });
    }, [
        waterfall < 5 ? waterfall : 5
    ]);
    return 'waterfall: ' + waterfall;
};
